import type { AnyBackendMessageBody } from '@post-office/placement-common-v2/types';

/** Messages with subscriptionConfig enabling live-only or live-and-persisted delivery get the first slot when present. */
const isLiveSubscriptionDelivery = (m: AnyBackendMessageBody): boolean => {
	const { subscriptionConfig } = m;
	if (!subscriptionConfig) {
		return false;
	}
	const { deliveryMechanism } = subscriptionConfig;
	return deliveryMechanism === 'live-only' || deliveryMechanism === 'live-and-persisted';
};

/**
 * Selects up to 2 messages for the rovo-nudge placement:
 * - Max 1 prioritized message (latest by eventTime, subscriptionConfig.deliveryMechanism is `live-only` or `live-and-persisted`)
 * - Max 1 other message (latest by eventTime)
 * - Prioritized subscription message is first in the result array
 *
 * Edge cases:
 * - If only live subscription messages exist: returns 1 such message
 * - If only non-live messages exist: returns 1 message
 * - If no messages at all: returns []
 */

const getLatestByEventTime = <T extends AnyBackendMessageBody>(messages: T[]): T | undefined => {
	let best: T | undefined;
	for (const current of messages) {
		if (!best || new Date(current.eventTime) > new Date(best.eventTime)) {
			best = current;
		}
	}
	return best;
};

export const selectRovoNudgeMessages = <T extends AnyBackendMessageBody>(messages: T[]): T[] => {
	const subscriptionMessages = messages.filter(isLiveSubscriptionDelivery);
	const normalMessages = messages.filter((m) => !isLiveSubscriptionDelivery(m));

	const topSubscription = getLatestByEventTime(subscriptionMessages);
	const topNormal = getLatestByEventTime(normalMessages);

	// Priority: subscription first, then normal — filter out undefined entries
	const result = [topSubscription, topNormal].filter((m): m is T => m !== undefined);

	// Edge case: no messages matched either bucket (e.g. empty input)
	return result;
};
